import useIfCanSendOrder, { incrementOrder } from 'helpers/testIfCanSendOrder'
import React, { createContext, useContext, useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import { useHistory } from 'react-router'
import CartService from 'services/CartService'
import useDeviceDetect from '../components/DeviceDetect/DeviceDetect'
import useAuth from './AuthContext'
import useOrders from './OrdersProvider'
import useProduct from './SingleProductProvider'
import useAffiliate from './AffilateContext'

const CartContext = createContext('config')

export const CartContextProvider = ({ children }) => {
  const history = useHistory()
  const { isMobileOrTablet } = useDeviceDetect()
  const [isLoaded, setLoading] = useState(false)
  const [products, setProducts] = useState([])
  const [meta, setMeta] = useState({})
  const [orderModalOpen, setOrderModalOpen] = useState(false)
  const [payMethod, _setPayMethod] = useState('card')
  const [bank, _setBank] = useState('tbc')
  const {
    product: { price: currentProductPrice },
  } = useProduct()
  const { auth } = useAuth()
  const { fetchOrders } = useOrders()
  const canSendOrder = useIfCanSendOrder()
  const { getAffiliateId } = useAffiliate()

  const fetchCart = () => {
    setLoading(true)
    CartService.getCart({
      affid: getAffiliateId(),
    })
      .then(c => {
        ReactDOM.unstable_batchedUpdates(() => {
          setLoading(false)
          if (!Array.isArray(c.products)) return
          setProducts(c.products)
          setMeta(c.meta)

          const normalizedPayMethod =
            c.meta.pay_method && c.meta.pay_method !== 'cash'
              ? c.meta.pay_method
              : 'card'

          _setPayMethod(normalizedPayMethod)

          if (c.meta.pay_method !== normalizedPayMethod) {
            CartService.changePayMethod(normalizedPayMethod)
          }

          _setBank(c.meta.bank)
        })
      })
      .catch(() => {
        setLoading(false)
      })
  }

  const addToCart = (sku, variants, showModal, redirect) => {
    return CartService.addToCart(sku, variants).then(() => {
      fetchCart()
      if (redirect) {
        history.push('/purchase')
      }
    })
  }

  const changeQuantity = (sku, quantity) => {
    CartService.updateQuantity(sku, quantity).then(() => {
      fetchCart()
    })
  }

  const deleteProduct = sku => {
    return CartService.deleteProduct(sku).then(() => fetchCart())
  }

  const setOrderModal = (b = true) => {
    if (!isMobileOrTablet) {
      setOrderModalOpen(b)
    } else {
      history.push('/purchase')
    }
  }

  const setPayMethod = payMethod => {
    const normalizedPayMethod = payMethod === 'cash' ? 'card' : payMethod
    CartService.changePayMethod(normalizedPayMethod).then(() => {
      _setPayMethod(normalizedPayMethod)
      fetchCart()
    })
  }

  const chooseBank = bank => {
    CartService.changeBank(bank).then(() => {
      _setBank(bank)
    })
  }

  const setDeliveryType = deliveryType => {
    CartService.changeDeliveryType(deliveryType).then(() => {
      fetchCart()
    })
  }

  const placeOrder = () => {
    CartService.placeOrder().then(data => {
      incrementOrder()
      fetchCart()
      fetchOrders()
    })
  }

  const applyCardCouponCode = code => {
    CartService.applySaleCode(code).then(() => {
      fetchCart()
    })
  }

  const removeCouponCode = code => {
    CartService.removeCouponCode().then(() => {
      fetchCart()
    })
  }

  useEffect(() => {
    fetchCart()
  }, [auth])

  useEffect(() => {
    if (products.length > 0) return

    setOrderModalOpen(false)
  }, [products])

  useEffect(() => {
    if (
      meta.price < 80 &&
      currentProductPrice < 80 &&
      payMethod === 'installment'
    ) {
      setPayMethod('card')
    }
  }, [payMethod, meta.price, currentProductPrice])

  return (
    <CartContext.Provider
      value={{
        loading: !isLoaded,
        products: products,
        orderModalOpen: orderModalOpen,
        payMethod: payMethod,
        bank: bank,
        meta: meta,
        fetchCart: fetchCart,
        placeOrder: placeOrder,
        addToCart: addToCart,
        setOrderModal: setOrderModal,
        applyCardCouponCode: applyCardCouponCode,
        removeCouponCode: removeCouponCode,
        changeQuantity: changeQuantity,
        deleteProduct: deleteProduct,
        setPayMethod: setPayMethod,
        chooseBank: chooseBank,
        setDeliveryType: setDeliveryType,
        canSendOrder: canSendOrder,
        installmentAvialable: meta.price >= 80,
        isInstallment: payMethod === 'installment',
      }}
    >
      {children}
    </CartContext.Provider>
  )
}

const useCart = () => useContext(CartContext)

export default useCart
